import React, { useState, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { useServiceCostStore } from '../../lib/stores/serviceCostStore';
import { useServiceCostTemplateStore } from '../../lib/stores/serviceCostTemplateStore';
import { useServiceStore } from '../../lib/stores/serviceStore';
import { usePartnerStore } from '../../lib/stores/partnerStore';
import { useDealStore } from '../../lib/stores/dealStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
// Removed shadcn/ui Select import - using HTML select instead
import { SearchableSelect } from '../ui/searchable-select';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { formatCurrency } from '../../lib/utils';
import type { ServiceCost, ServiceCostFormData } from '../../lib/types';

interface ServiceCostFormProps {
  cost?: ServiceCost | null;
  serviceId?: string;
  dealId?: string;
  mode: 'template' | 'actual'; // Nuova prop per distinguere la modalità
  onSave: () => void;
  onCancel: () => void;
}

export function ServiceCostForm({ cost, serviceId, dealId, mode, onSave, onCancel }: ServiceCostFormProps) {
  const serviceCostStore = useServiceCostStore();
  const serviceCostTemplateStore = useServiceCostTemplateStore();

  // Select the appropriate store based on mode
  const { categories, fetchCategories, createCost, updateCost, loading, error, clearError } = mode === 'template' ? {
    categories: serviceCostTemplateStore.costCategories,
    fetchCategories: serviceCostTemplateStore.fetchCostCategories,
    createCost: async (data: Partial<ServiceCostFormData>) => {
      if (!serviceId) throw new Error('Service ID required for template');

      // DEBUG: Log dei dati ricevuti
      console.log('🔍 ServiceCostForm - Data ricevuta:', {
        estimated_amount: data.estimated_amount,
        calculation_type: data.calculation_type,
        hourly_rate: data.hourly_rate,
        hours_estimated: data.hours_estimated,
        unit_rate: data.unit_rate,
        estimated_units: data.estimated_units
      });

      // Convert ServiceCostFormData to ServiceCostTemplateFormData
      const templateData = {
        name: data.name || '',
        description: data.description,
        cost_category_id: data.cost_category_id,
        calculation_type: data.calculation_type || 'fixed',
        default_amount: data.estimated_amount !== undefined ? data.estimated_amount : 0,
        // Hourly calculation fields
        hourly_rate: data.hourly_rate !== undefined ? data.hourly_rate : 0,
        hours_estimated: data.hours_estimated !== undefined ? data.hours_estimated : 0,
        // Per unit calculation fields
        unit_rate: data.unit_rate !== undefined ? data.unit_rate : 0,
        estimated_units: data.estimated_units !== undefined ? data.estimated_units : 1,
        // Partner fields
        external_partner_id: data.external_partner_id && data.external_partner_id !== 'none' ? data.external_partner_id : undefined,
        partner_cost: data.partner_cost || 0,
        partner_markup_percentage: data.partner_markup_percentage || 0,
        // Other fields
        status: data.status || 'pending',
        is_billable: data.is_billable !== undefined ? data.is_billable : true,
        is_required: false,
        is_visible_to_client: data.is_visible_to_client || false,
        include_in_pdf: data.include_in_pdf || false,
        cost_display_name: data.cost_display_name,
        sort_order: 0
      };

      // DEBUG: Log dei dati che verranno inviati
      console.log('📤 Template data da inviare a Supabase:', templateData);

      const result = await serviceCostTemplateStore.createTemplate(serviceId, templateData);

      // DEBUG: Log del risultato
      console.log('✅ Template creato:', result);

      return result;
    },
    updateCost: async (id: string, data: Partial<ServiceCostFormData>) => {
      // Convert ServiceCostFormData to ServiceCostTemplateFormData
      const templateData: any = {};
      if (data.name !== undefined) templateData.name = data.name;
      if (data.description !== undefined) templateData.description = data.description;
      if (data.cost_category_id !== undefined) templateData.cost_category_id = data.cost_category_id;
      if (data.calculation_type !== undefined) templateData.calculation_type = data.calculation_type;
      if (data.estimated_amount !== undefined) templateData.default_amount = data.estimated_amount;
      // Hourly calculation fields
      if (data.hourly_rate !== undefined) templateData.hourly_rate = data.hourly_rate;
      if (data.hours_estimated !== undefined) templateData.hours_estimated = data.hours_estimated;
      // Per unit calculation fields
      if (data.unit_rate !== undefined) templateData.unit_rate = data.unit_rate;
      if (data.estimated_units !== undefined) templateData.estimated_units = data.estimated_units;
      // Partner fields
      if (data.external_partner_id !== undefined) {
        templateData.external_partner_id = data.external_partner_id && data.external_partner_id !== 'none' ? data.external_partner_id : null;
      }
      if (data.partner_cost !== undefined) templateData.partner_cost = data.partner_cost;
      if (data.partner_markup_percentage !== undefined) templateData.partner_markup_percentage = data.partner_markup_percentage;
      // Other fields
      if (data.status !== undefined) templateData.status = data.status;
      if (data.is_billable !== undefined) templateData.is_billable = data.is_billable;
      if (data.is_visible_to_client !== undefined) templateData.is_visible_to_client = data.is_visible_to_client;
      if (data.include_in_pdf !== undefined) templateData.include_in_pdf = data.include_in_pdf;
      if (data.cost_display_name !== undefined) templateData.cost_display_name = data.cost_display_name;
      return await serviceCostTemplateStore.updateTemplate(id, templateData);
    },
    loading: serviceCostTemplateStore.loading,
    error: serviceCostTemplateStore.error,
    clearError: () => {} // Template store doesn't have clearError, no-op
  } : serviceCostStore;

  const { services, fetchServices } = useServiceStore();
  const { partners, fetchPartners } = usePartnerStore();
  const { deals, fetchAllDeals } = useDealStore();

  const [formData, setFormData] = useState<Partial<ServiceCostFormData>>({
    name: '',
    description: '',
    service_id: serviceId || '',
    deal_id: dealId || '',
    cost_category_id: '',
    cost_type: mode === 'template' ? 'estimated' : 'actual',
    calculation_type: 'fixed',
    estimated_amount: 0,
    actual_amount: 0,
    budgeted_amount: 0,
    estimated_units: 1,
    actual_units: 0,
    unit_rate: 0,
    hourly_rate: 0,
    hours_estimated: 0,
    hours_actual: 0,
    external_partner_id: 'none',
    partner_cost: 0,
    partner_markup_percentage: 0,
    status: mode === 'template' ? 'pending' : 'approved',
    is_billable: true,
    affects_commission: false,
    markup_type: 'none',
    markup_percentage: 0,
    markup_amount: 0,
    client_price: 0,
    has_markup: false,
    is_visible_to_client: false,
    include_in_pdf: false,
    cost_display_name: '',
    internal_notes: '',
    incurred_date: mode === 'actual' ? new Date().toISOString().split('T')[0] : '',
    due_date: '',
    invoice_reference: ''
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);

  // For actual costs: available deals and their services
  const [selectedDealId, setSelectedDealId] = useState<string>(dealId || '');
  const [availableDealServices, setAvailableDealServices] = useState<any[]>([]);

  useEffect(() => {
    if (cost) {
      setFormData({
        name: cost.name,
        description: cost.description || '',
        service_id: cost.service_id,
        deal_id: cost.deal_id || '',
        cost_category_id: cost.cost_category_id,
        cost_type: cost.cost_type,
        calculation_type: cost.calculation_type,
        // For templates, map default_amount to estimated_amount
        estimated_amount: mode === 'template' ? (cost.default_amount || 0) : (cost.estimated_amount || 0),
        actual_amount: cost.actual_amount || 0,
        budgeted_amount: cost.budgeted_amount || 0,
        estimated_units: cost.estimated_units || 1,
        actual_units: cost.actual_units || 0,
        unit_rate: cost.unit_rate || 0,
        hourly_rate: cost.hourly_rate || 0,
        hours_estimated: cost.hours_estimated || 0,
        hours_actual: cost.hours_actual || 0,
        external_partner_id: cost.external_partner_id || 'none',
        partner_cost: cost.partner_cost || 0,
        partner_markup_percentage: cost.partner_markup_percentage || 0,
        status: cost.status || 'pending',
        is_billable: cost.is_billable !== undefined ? cost.is_billable : true,
        affects_commission: cost.affects_commission || false,
        markup_type: cost.markup_type || 'none',
        markup_percentage: cost.markup_percentage || 0,
        markup_amount: cost.markup_amount || 0,
        client_price: cost.client_price || 0,
        has_markup: cost.has_markup || false,
        is_visible_to_client: cost.is_visible_to_client || false,
        include_in_pdf: cost.include_in_pdf || false,
        cost_display_name: cost.cost_display_name || '',
        internal_notes: cost.internal_notes || '',
        incurred_date: cost.incurred_date || '',
        due_date: cost.due_date || '',
        invoice_reference: cost.invoice_reference || ''
      });
    }

    if (services.length === 0) fetchServices();
    if (categories.length === 0) fetchCategories();
    if (partners.length === 0) fetchPartners();
    if (mode === 'actual' && deals.length === 0) fetchAllDeals();
  }, [cost]);

  // Initialize selectedDealId when dealId prop is provided
  useEffect(() => {
    if (dealId && mode === 'actual') {
      setSelectedDealId(dealId);
    }
  }, [dealId, mode]);

  // Pre-load deal services when we have a specific dealId
  useEffect(() => {
    if (dealId && mode === 'actual' && deals.length > 0) {
      const currentDeal = deals.find(deal => deal.id === dealId);
      if (currentDeal && currentDeal.deal_services) {
        setAvailableDealServices(currentDeal.deal_services);
      } else if (currentDeal) {
        // If deal exists but deal_services is not loaded, we may need to fetch it
        // For now, set empty array to avoid errors
        setAvailableDealServices([]);
      }
    }
  }, [dealId, deals, mode]);

  // Update available deal services when deal is selected
  useEffect(() => {
    if (selectedDealId && deals.length > 0) {
      const selectedDeal = deals.find(deal => deal.id === selectedDealId);
      if (selectedDeal && selectedDeal.deal_services) {
        setAvailableDealServices(selectedDeal.deal_services);
      } else {
        setAvailableDealServices([]);
      }
    } else {
      setAvailableDealServices([]);
    }
  }, [selectedDealId, deals]);

  // Update formData when deal selection changes
  useEffect(() => {
    if (mode === 'actual') {
      setFormData(prev => ({
        ...prev,
        deal_id: selectedDealId || null
      }));
    }
  }, [selectedDealId, mode]);

  // Calculate client price based on markup
  const calculateClientPrice = (baseAmount: number, markupType: string, markupPercentage: number = 0, markupAmount: number = 0): number => {
    switch (markupType) {
      case 'percentage':
        return baseAmount * (1 + markupPercentage / 100);
      case 'fixed':
        return baseAmount + markupAmount;
      default:
        return baseAmount;
    }
  };

  // Update client_price when markup or base amount changes
  useEffect(() => {
    const baseAmount = mode === 'actual' 
      ? (formData.actual_amount || formData.estimated_amount || 0)
      : (formData.estimated_amount || 0);
    
    const clientPrice = calculateClientPrice(
      baseAmount,
      formData.markup_type || 'none',
      formData.markup_percentage || 0,
      formData.markup_amount || 0
    );

    setFormData(prev => ({
      ...prev,
      client_price: clientPrice,
      has_markup: (formData.markup_type !== 'none' && formData.markup_type !== undefined)
    }));
  }, [formData.estimated_amount, formData.actual_amount, formData.markup_type, formData.markup_percentage, formData.markup_amount, mode]);

  const handleInputChange = (field: keyof ServiceCostFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      errors.name = 'Il nome è obbligatorio';
    }

    // For template mode, service_id is always required
    // For actual mode, service_id is optional if we have a dealId (deal-level costs)
    if (mode === 'template' && !formData.service_id) {
      errors.service_id = 'Il servizio è obbligatorio';
    }

    // For actual costs, require deal_id
    if (mode === 'actual' && !selectedDealId && !dealId) {
      errors.deal_id = 'Il contratto è obbligatorio per i costi effettivi';
    }

    if (!formData.cost_category_id) {
      errors.cost_category_id = 'La categoria è obbligatoria';
    }

    if (formData.calculation_type === 'fixed' && !formData.estimated_amount) {
      errors.estimated_amount = 'L\'importo stimato è obbligatorio per il tipo fisso';
    }

    if (formData.calculation_type === 'hourly' && (!formData.hourly_rate || !formData.hours_estimated)) {
      errors.hourly_rate = 'La tariffa oraria e le ore stimate sono obbligatorie per il tipo orario';
    }

    if (formData.calculation_type === 'per_unit' && (!formData.unit_rate || !formData.estimated_units)) {
      errors.unit_rate = 'La tariffa unitaria e le unità stimate sono obbligatorie per il tipo per unità';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateAmounts = () => {
    let estimatedAmount = 0;

    switch (formData.calculation_type) {
      case 'fixed':
        estimatedAmount = formData.estimated_amount || 0;
        break;
      case 'hourly':
        estimatedAmount = (formData.hourly_rate || 0) * (formData.hours_estimated || 0);
        break;
      case 'per_unit':
        estimatedAmount = (formData.unit_rate || 0) * (formData.estimated_units || 0);
        break;
      case 'percentage':
        // For percentage, we would need the base amount from service
        estimatedAmount = formData.estimated_amount || 0;
        break;
    }

    // Add partner costs
    if (formData.external_partner_id && formData.external_partner_id !== 'none' && formData.partner_cost) {
      const markup = (formData.partner_markup_percentage || 0) / 100;
      estimatedAmount += formData.partner_cost * (1 + markup);
    }

    return estimatedAmount;
  };

  useEffect(() => {
    if (formData.calculation_type !== 'fixed') {
      const calculatedAmount = calculateAmounts();
      setFormData(prev => ({
        ...prev,
        estimated_amount: calculatedAmount
      }));
    }
  }, [
    formData.calculation_type,
    formData.hourly_rate,
    formData.hours_estimated,
    formData.unit_rate,
    formData.estimated_units,
    formData.partner_cost,
    formData.partner_markup_percentage
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    // For non-fixed calculation types, ensure estimated_amount is calculated before submitting
    let finalFormData = { ...formData };
    if (formData.calculation_type !== 'fixed') {
      const calculatedAmount = calculateAmounts();
      finalFormData = {
        ...formData,
        estimated_amount: calculatedAmount
      };
    }

    if (!validateForm()) {
      return;
    }

    // Clean form data - convert empty strings and 'none' to null for UUID fields
    const cleanedFormData = {
      ...finalFormData,
      // Clean UUID fields - convert empty strings and 'none' to null
      service_id: formData.service_id && formData.service_id !== '' ? formData.service_id : null,
      deal_id: (formData.deal_id && formData.deal_id !== '') ? formData.deal_id : (dealId || null),
      cost_category_id: formData.cost_category_id, // Required field, keep as is
      external_partner_id: formData.external_partner_id && formData.external_partner_id !== '' && formData.external_partner_id !== 'none' ? formData.external_partner_id : null,
      // Clean date fields - convert empty strings to null
      incurred_date: formData.incurred_date && formData.incurred_date !== '' ? formData.incurred_date : null,
      due_date: formData.due_date && formData.due_date !== '' ? formData.due_date : null,
      // Clean string fields - convert empty strings to null
      invoice_reference: formData.invoice_reference && formData.invoice_reference !== '' ? formData.invoice_reference : null,
      description: formData.description && formData.description !== '' ? formData.description : null
    };

    const success = cost
      ? await updateCost(cost.id, cleanedFormData)
      : await createCost(cleanedFormData);

    if (success) {
      onSave();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nome */}
        <div className="md:col-span-2">
          <Label htmlFor="name">Nome Costo *</Label>
          <Input
            id="name"
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={validationErrors.name ? 'border-red-500' : ''}
            placeholder="Es: Sviluppo Frontend, Consulenza SEO..."
          />
          {validationErrors.name && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
          )}
        </div>

        {/* Descrizione */}
        <div className="md:col-span-2">
          <Label htmlFor="description">Descrizione</Label>
          <Textarea
            id="description"
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            placeholder="Descrizione dettagliata del costo..."
          />
        </div>

        {/* Selezione servizio solo per template mode senza serviceId */}
        {mode === 'template' && !serviceId && (
          <div>
            <Label htmlFor="service_id">Servizio *</Label>
            <SearchableSelect
              value={formData.service_id || ''}
              onValueChange={(value) => handleInputChange('service_id', value)}
              options={services.length === 0 ? [
                { value: 'loading', label: 'Caricamento servizi...', disabled: true }
              ] : services.map((service) => ({
                value: service.id,
                label: service.title
              }))}
              placeholder="Seleziona servizio"
              className={validationErrors.service_id ? 'border-red-500' : ''}
            />
            {validationErrors.service_id && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.service_id}</p>
            )}
          </div>
        )}

        {/* Categoria Costo */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <Label htmlFor="cost_category_id">Categoria Costo *</Label>
            <div className="relative">
              <HelpCircle
                className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                onClick={() => setOpenTooltip(openTooltip === 'category' ? null : 'category')}
              />
              {openTooltip === 'category' && (
                <div className="absolute left-0 top-6 z-[10000] w-64 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg text-sm text-gray-700 dark:text-gray-300">
                  Seleziona la categoria che meglio descrive questo costo (es. Materiali, Manodopera, Partner, ecc.)
                  <button
                    onClick={() => setOpenTooltip(null)}
                    className="absolute top-1 right-1 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>
          <SearchableSelect
            value={formData.cost_category_id || ''}
            onValueChange={(value) => handleInputChange('cost_category_id', value)}
            options={categories.length === 0 ? [
              { value: 'loading', label: 'Caricamento categorie...', disabled: true }
            ] : categories.map((category) => ({
              value: category.id,
              label: `${category.name} (${category.category_type})`
            }))}
            placeholder="Seleziona categoria"
            className={validationErrors.cost_category_id ? 'border-red-500' : ''}
          />
          {validationErrors.cost_category_id && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.cost_category_id}</p>
          )}
        </div>

        {/* Tipo Calcolo */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <Label htmlFor="calculation_type">Tipo di Calcolo *</Label>
            <div className="relative">
              <HelpCircle
                className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                onClick={() => setOpenTooltip(openTooltip === 'calculation' ? null : 'calculation')}
              />
              {openTooltip === 'calculation' && (
                <div className="absolute left-0 top-6 z-[10000] w-64 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg text-sm text-gray-700 dark:text-gray-300">
                  <strong>Fisso:</strong> importo sempre uguale<br/>
                  <strong>Orario:</strong> tariffa × ore<br/>
                  <strong>Per Unità:</strong> prezzo × quantità<br/>
                  <strong>Percentuale:</strong> % sul prezzo servizio
                  <button
                    onClick={() => setOpenTooltip(null)}
                    className="absolute top-1 right-1 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>
          <select
            id="calculation_type"
            value={formData.calculation_type || 'fixed'}
            onChange={(e) => handleInputChange('calculation_type', e.target.value)}
            className="block w-full pl-3 pr-10 py-2 glass-card rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          >
            <option value="fixed">💰 Importo Fisso</option>
            <option value="hourly">⏱️ Tariffa Oraria</option>
            <option value="per_unit">📦 Prezzo per Unità</option>
            <option value="percentage">📊 Percentuale</option>
          </select>
        </div>

      </div>

      {/* Importo e Partner affiancati */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Colonna Importo */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              {mode === 'template' ? 'Importo' : 'Importi e Calcoli'}
            </h4>
            <div className="relative">
              <HelpCircle
                className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                onClick={() => setOpenTooltip(openTooltip === 'amount' ? null : 'amount')}
              />
              {openTooltip === 'amount' && (
                <div className="absolute right-0 top-6 z-[10000] w-64 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg text-sm text-gray-700 dark:text-gray-300">
                  L'importo dipende dal tipo di calcolo selezionato. Per calcoli dinamici (orario, per unità) il totale viene calcolato automaticamente.
                  <button
                    onClick={() => setOpenTooltip(null)}
                    className="absolute top-1 right-1 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>

          {formData.calculation_type === 'fixed' && (
            <div>
              <Label htmlFor="estimated_amount">{mode === 'template' ? 'Importo *' : 'Importo Stimato *'}</Label>
              <Input
                id="estimated_amount"
                type="number"
                step="0.01"
                value={formData.estimated_amount || ''}
                onChange={(e) => handleInputChange('estimated_amount', parseFloat(e.target.value) || 0)}
                className={validationErrors.estimated_amount ? 'border-red-500' : ''}
                placeholder="0.00"
              />
              {validationErrors.estimated_amount && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.estimated_amount}</p>
              )}
            </div>
          )}

          {formData.calculation_type === 'hourly' && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="hourly_rate">Tariffa Oraria (€/h) *</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  value={formData.hourly_rate || ''}
                  onChange={(e) => handleInputChange('hourly_rate', parseFloat(e.target.value) || 0)}
                  className={validationErrors.hourly_rate ? 'border-red-500' : ''}
                  placeholder="0.00"
                />
                {validationErrors.hourly_rate && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.hourly_rate}</p>
                )}
              </div>
              <div>
                <Label htmlFor="hours_estimated">{mode === 'template' ? 'Ore Previste *' : 'Ore Stimate *'}</Label>
                <Input
                  id="hours_estimated"
                  type="number"
                  step="0.5"
                  value={formData.hours_estimated || ''}
                  onChange={(e) => handleInputChange('hours_estimated', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>
              <div className="p-2 bg-blue-50 dark:bg-blue-900 rounded text-sm text-blue-800 dark:text-blue-200">
                <strong>Totale:</strong> €{calculateAmounts().toFixed(2)}
              </div>
            </div>
          )}

          {formData.calculation_type === 'per_unit' && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="unit_rate">Tariffa Unitaria (€) *</Label>
                <Input
                  id="unit_rate"
                  type="number"
                  step="0.01"
                  value={formData.unit_rate || ''}
                  onChange={(e) => handleInputChange('unit_rate', parseFloat(e.target.value) || 0)}
                  className={validationErrors.unit_rate ? 'border-red-500' : ''}
                  placeholder="0.00"
                />
                {validationErrors.unit_rate && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.unit_rate}</p>
                )}
              </div>
              <div>
                <Label htmlFor="estimated_units">{mode === 'template' ? 'Quantità Prevista *' : 'Unità Stimate *'}</Label>
                <Input
                  id="estimated_units"
                  type="number"
                  value={formData.estimated_units || ''}
                  onChange={(e) => handleInputChange('estimated_units', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>
              <div className="p-2 bg-blue-50 dark:bg-blue-900 rounded text-sm text-blue-800 dark:text-blue-200">
                <strong>Totale:</strong> €{calculateAmounts().toFixed(2)}
              </div>
            </div>
          )}

          {formData.calculation_type === 'percentage' && (
            <div>
              <Label htmlFor="estimated_amount">Percentuale (%) *</Label>
              <Input
                id="estimated_amount"
                type="number"
                step="0.01"
                value={formData.estimated_amount || ''}
                onChange={(e) => handleInputChange('estimated_amount', parseFloat(e.target.value) || 0)}
                className={validationErrors.estimated_amount ? 'border-red-500' : ''}
                placeholder="0.00"
              />
              {validationErrors.estimated_amount && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.estimated_amount}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Verrà calcolata sul prezzo del servizio</p>
            </div>
          )}

        </div>

        {/* Colonna Partner */}
        <div>
          <div className="flex items-center gap-1 mb-2">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Partner Esterno</h4>
            <div className="relative">
              <HelpCircle
                className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                onClick={() => setOpenTooltip(openTooltip === 'partner' ? null : 'partner')}
              />
              {openTooltip === 'partner' && (
                <div className="absolute right-0 top-6 z-[10000] w-64 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg text-sm text-gray-700 dark:text-gray-300">
                  Seleziona un partner esterno se questo costo viene gestito da un fornitore terzo. L'importo del costo sarà quello specificato sopra.
                  <button
                    onClick={() => setOpenTooltip(null)}
                    className="absolute top-1 right-1 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="external_partner_id">Partner (Opzionale)</Label>
            <SearchableSelect
              value={formData.external_partner_id || ''}
              onValueChange={(value) => handleInputChange('external_partner_id', value)}
              options={[
                { value: 'none', label: 'Nessun partner' },
                ...(partners.length === 0 ? [
                  { value: 'loading', label: 'Caricamento partner...', disabled: true }
                ] : partners.map((partner) => ({
                  value: partner.id,
                  label: partner.name + (partner.company ? ` (${partner.company})` : '')
                })))
              ]}
              placeholder="Nessun partner"
            />
          </div>
        </div>
      </div>

      {/* Altri Dettagli */}
      {mode === 'template' ? (
        <div className="flex items-center justify-between">
          <Label htmlFor="status">Stato</Label>
          <button
            type="button"
            onClick={() => handleInputChange('status', formData.status === 'pending' ? 'cancelled' : 'pending')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${
              formData.status === 'pending' ? 'bg-green-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.status === 'pending' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {formData.status === 'pending' ? 'Abilitato' : 'Disabilitato'}
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="status">Stato</Label>
            <select
              id="status"
              value={formData.status || 'pending'}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="block w-full pl-3 pr-10 py-2 glass-card rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            >
              <option value="pending">In Attesa di Approvazione</option>
              <option value="approved">Approvato</option>
              <option value="review_needed">Da Verificare</option>
            </select>
          </div>

          <div>
            <Label htmlFor="invoice_reference">Riferimento Fattura</Label>
            <Input
              id="invoice_reference"
              value={formData.invoice_reference || ''}
              onChange={(e) => handleInputChange('invoice_reference', e.target.value)}
              placeholder="Numero fattura o riferimento esterno..."
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button 
          type="submit" 
          disabled={loading}
          className="bg-[#0f6cbd] hover:bg-[#0a5ba8] text-white"
        >
          {loading ? 'Salvataggio...' : (cost ? 'Aggiorna Costo' : 'Crea Costo')}
        </Button>
      </div>
    </form>
  );
}